import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Alert, Drawer, Pagination, Timeline } from 'antd'
import { getOffsetOfFirstAndLastRecord } from '@Builder/QuotesHomePage/logic'

import Box from '~/core/Box'
import Button from '~/core/Button'
import { getUserIdentity } from '~/redux-modules/identity'
import { DEFAULT_REFRESH_MSG } from '~/lib/flashMsgs'
import {
    OrderActivityItemFragment,
    OrderActivityQueryDocument,
    QuoteActivityItemFragment,
    QuoteActivityQueryDocument,
} from '~/gql/graphql'

import ActivityLogItem from './ActivityLogItem'

const BODY_PADDING_STYLE = { padding: '25px 12px' }
const DEFAULT_PAGE_NUMBER = 1
const PADDING_STYLE = { padding: '16px 12px' }
const PAGINATION_LIMIT = 8
const PLACEMENT = 'quote-activity-logs'
const WIDTH = 370

export type ActivityType = QuoteActivityItemFragment | OrderActivityItemFragment

type ActivityLogsPropsType = {
    quoteId?: string
    saleOrderId?: string
}

// Contains the View activity log link
// as well as the Activity Log drawer
const ActivityLogs = (props: ActivityLogsPropsType) => {
    const { quoteId, saleOrderId } = props

    const { userId: isLocatedInObo = '' } = useParams<{
        userId?: string
    }>()

    const [isActivityLogVisible, setIsActivityLogVisible] = useState(false)
    const [pageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER)

    const identity = useSelector(getUserIdentity)

    const upcomingPageOffset = PAGINATION_LIMIT * (pageNumber - 1)

    const { data: quoteActivityData, error: quoteActivityError } = useQuery(
        QuoteActivityQueryDocument,
        {
            variables: {
                quoteId,
                showDetailedLogs: identity.isAdmin && !isLocatedInObo,
                limit: PAGINATION_LIMIT,
                offset: upcomingPageOffset,
            },
            skip: !quoteId || !isActivityLogVisible,
            fetchPolicy: 'network-only',
        },
    )

    // salesOrder.id looks like either: erp:101 or platform:101
    // where a saleOrder with platform:101 has not been imported into odoo
    // and erp:101 has been imported into odoo and picked up by airflow
    //
    // orderActivity query requires the variable saleOrderId to be a number
    let syncedOrderId

    if (saleOrderId && saleOrderId?.indexOf('erp') === 0) {
        const numericalOrderId = saleOrderId?.split(':')[1]

        syncedOrderId = parseInt(numericalOrderId, 10)
    }

    const { data: orderActivityData, error: orderActivityError } = useQuery(
        OrderActivityQueryDocument,
        {
            variables: {
                saleOrderId: syncedOrderId,
                limit: PAGINATION_LIMIT,
                offset: upcomingPageOffset,
            },
            skip: !syncedOrderId || !isActivityLogVisible,
            fetchPolicy: 'network-only',
        },
    )

    // activity items are not available when
    // the order has not been imported into odoo
    // so we should not show the 'View activity logs` link
    if (saleOrderId && !syncedOrderId) {
        return null
    }

    const activityItems =
        quoteActivityData?.quoteActivity?.items ||
        orderActivityData?.orderActivity?.items ||
        []
    const totalCount =
        quoteActivityData?.quoteActivity?.totalCount ||
        orderActivityData?.orderActivity?.totalCount
    const error = quoteActivityError || orderActivityError

    const numberOfRecordsOnPage = activityItems.length
    const enablePagination = totalCount > PAGINATION_LIMIT

    const { offsetOfFirstRecordOfPage, offsetOfLastRecordOfPage } =
        getOffsetOfFirstAndLastRecord(
            pageNumber,
            numberOfRecordsOnPage,
            PAGINATION_LIMIT,
        )

    const getShowTotalPages = (total: number) =>
        `${offsetOfFirstRecordOfPage}-${offsetOfLastRecordOfPage} of ${total}`

    const onChangePage = (upcomingPageNumber: number) => {
        setPageNumber(upcomingPageNumber)
    }

    return (
        <Box data-test-target={`${PLACEMENT}-info`}>
            <Button
                data-test-target={PLACEMENT}
                name="activity-log"
                placement={PLACEMENT}
                type="link"
                onClick={(evt: Event) => {
                    evt.preventDefault()
                    setIsActivityLogVisible(true)
                }}
                style={{ padding: 0 }}
            >
                View activity log
            </Button>
            <Drawer
                bodyStyle={{ flex: 1, ...BODY_PADDING_STYLE }}
                headerStyle={PADDING_STYLE}
                onClose={() => setIsActivityLogVisible(false)}
                title="Activity log"
                visible={isActivityLogVisible}
                width={WIDTH}
                drawerStyle={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {error ? (
                    <Alert
                        message={DEFAULT_REFRESH_MSG}
                        type="error"
                        showIcon
                    />
                ) : (
                    <Timeline>
                        {activityItems.map(
                            (activityItem: ActivityType, idx: number) => (
                                <ActivityLogItem
                                    activityItem={activityItem}
                                    key={`${idx}-${activityItem.createdAt}`}
                                />
                            ),
                        )}
                    </Timeline>
                )}
                <Box display="flex" justifyContent="end" marginBottom="128px">
                    {enablePagination && (
                        <Pagination
                            current={pageNumber || DEFAULT_PAGE_NUMBER}
                            defaultPageSize={PAGINATION_LIMIT}
                            onChange={onChangePage}
                            pageSize={PAGINATION_LIMIT}
                            showTotal={getShowTotalPages}
                            showSizeChanger={false}
                            size="small"
                            total={totalCount}
                        />
                    )}
                </Box>
            </Drawer>
        </Box>
    )
}

export default ActivityLogs
